/**
 * AnimationSettingsProvider component
 *
 * A context provider that manages application-wide animation settings
 * with focus on accessibility and user preferences.
 */
import React, { useState, useCallback, createContext, ReactNode, useEffect, useMemo, useRef } from "react";

import { logger } from "../utils/logger";

// Create a namespaced logger for animation settings
const animationLogger = logger.getNamespacedLogger('AnimationSettings');

// Define animation intensity levels
export type AnimationIntensity = 'none' | 'reduced' | 'normal' | 'high';

// Define the context interface
export interface AnimationSettingsContextType {
  /** Whether animations are enabled (master toggle) */
  animationsEnabled: boolean;
  
  /** The current animation intensity level */
  intensity: AnimationIntensity;
  
  /** Toggle animations on/off */
  toggleAnimations: () => void;
  
  /** Set the animation intensity level */
  setIntensity: (intensity: AnimationIntensity) => void;
  
  /** Whether the user prefers reduced motion (from system settings) */
  prefersReducedMotion: boolean;
  
  /** Get duration multiplier based on current settings (0 = no animation, 1 = normal) */
  getDurationMultiplier: () => number;
  
  /** Get delay multiplier based on current settings */
  getDelayMultiplier: () => number;
  
  /** Utility to check if a specific animation type should run */
  shouldAnimate: (type: 'scroll' | 'hover' | 'page' | 'background') => boolean;
  
  /** Debug utility to log current animation settings to console */
  logCurrentSettings: () => void;
}

// Create the context with undefined as initial value
export const AnimationSettingsContext = createContext<AnimationSettingsContextType | undefined>(undefined);

interface AnimationSettingsProviderProps {
  /** Whether to respect the user's prefers-reduced-motion setting (default: true) */
  respectReducedMotion?: boolean;
  
  /** Enable debug logging (default: false) */
  debug?: boolean;
  
  /** Children components that will have access to the animation settings context */
  children: ReactNode;
}

/**
 * AnimationSettingsProvider component
 *
 * @param props - Component props
 * @returns AnimationSettingsProvider component
 */
const AnimationSettingsProvider: React.FC<AnimationSettingsProviderProps> = ({
  respectReducedMotion = true,
  debug = false,
  children
}) => {
  // Initialize state with props - forcing normal animation for now to debug
  const [animationsEnabled, setAnimationsEnabled] = useState<boolean>(true);
  const [intensity, setIntensityState] = useState<AnimationIntensity>('normal');
  const [prefersReducedMotion, setPrefersReducedMotion] = useState<boolean>(false);
  
  // Use ref to track first render
  const isFirstRender = useRef(true);
  
  // Debug logger for animation settings changes
  const logCurrentSettings = useCallback(() => {
    if (debug) {
      animationLogger.debug('Animation Settings', {
        animationsEnabled,
        intensity,
        prefersReducedMotion,
        durationMultiplier: animationsEnabled ? (intensity === 'none' ? 0 : (intensity === 'reduced' ? 0.5 : (intensity === 'normal' ? 1 : 1.5))) : 0
      });
    }
    
    return {
      animationsEnabled,
      intensity,
      prefersReducedMotion,
      durationMultiplier: animationsEnabled ? (intensity === 'none' ? 0 : (intensity === 'reduced' ? 0.5 : (intensity === 'normal' ? 1 : 1.5))) : 0
    };
  }, [animationsEnabled, intensity, prefersReducedMotion, debug]);
  
  // Detect prefers-reduced-motion preference
  useEffect(() => {
    if (respectReducedMotion) {
      // Check if the user prefers reduced motion
      const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      const initialValue = mediaQuery.matches;
      setPrefersReducedMotion(initialValue);
      
      // If reduced motion is preferred and we respect that setting, adjust the intensity
      if (initialValue) {
        setIntensityState('reduced');
        if (debug) animationLogger.debug('Detected prefers-reduced-motion, setting intensity to reduced');
      }
      
      // Listen for changes in preference
      const handleChange = (event: MediaQueryListEvent) => {
        setPrefersReducedMotion(event.matches);
        
        // Automatically adjust intensity if user changes their preference
        if (event.matches) {
          setIntensityState('reduced');
          if (debug) animationLogger.debug('User changed to prefers-reduced-motion, setting intensity to reduced');
        } else if (intensity === 'reduced') {
          // Only revert to normal if current setting is reduced
          setIntensityState('normal');
          if (debug) animationLogger.debug('User disabled prefers-reduced-motion, reverting to normal intensity');
        }
      };
      
      // Add event listener
      mediaQuery.addEventListener('change', handleChange);
      
      // Clean up
      return () => {
        mediaQuery.removeEventListener('change', handleChange);
      };
    }
  }, [respectReducedMotion, intensity, debug]);
  
  // Toggle animations enabled/disabled
  const toggleAnimations = useCallback(() => {
    setAnimationsEnabled(prev => {
      const newValue = !prev;
      if (debug) animationLogger.debug(`Animations toggled: ${newValue ? 'enabled' : 'disabled'}`);
      return newValue;
    });
  }, [debug]);
  
  // Set animation intensity
  const setIntensity = useCallback((newIntensity: AnimationIntensity) => {
    setIntensityState(newIntensity);
    if (debug) animationLogger.debug(`Animation intensity set to: ${newIntensity}`);
  }, [debug]);
  
  // Get duration multiplier based on settings - always return 1 for now to debug
  const getDurationMultiplier = useCallback(() => {
    // TEMPORARY: Force full animation for all elements
    return 1;
    
    // Original code commented out for testing
    /*
    if (!animationsEnabled) return 0;
    
    // Return different multipliers based on intensity
    switch (intensity) {
      case 'none': return 0;
      case 'reduced': return 0.5;
      case 'normal': return 1;
      case 'high': return 1.5;
      default: return 1;
    }
    */
  }, [/* animationsEnabled, intensity */]);
  
  // Get delay multiplier based on settings
  const getDelayMultiplier = useCallback(() => {
    if (!animationsEnabled) return 0;
    
    // Return different multipliers based on intensity
    switch (intensity) {
      case 'none': return 0;
      case 'reduced': return 0.3; // Reduced stagger for reduced motion
      case 'normal': return 1;
      case 'high': return 1.2;
      default: return 1;
    }
  }, [animationsEnabled, intensity]);
  
  // Check if specific animation types should run - always return true for scroll animations to debug
  const shouldAnimate = useCallback((type: 'scroll' | 'hover' | 'page' | 'background') => {
    // TEMPORARY: Force scroll animations to be enabled
    if (type === 'scroll') return true;
    
    // Original code for other animation types
    if (!animationsEnabled) return false;
    if (intensity === 'none') return false;
    
    // For reduced motion, allow certain animations
    if (intensity === 'reduced') {
      switch (type) {
        case 'hover': return true; // Allow hover animations
        case 'page': return true;  // Allow page transitions
        case 'background': return false; // Disable background animations
        default: return false;
      }
    }
    
    // For normal and high, allow all animations
    return true;
  }, [animationsEnabled, intensity]);
  
  // Log initial settings on mount if debug mode is enabled - only log once
  useEffect(() => {
    if (debug && isFirstRender.current) {
      animationLogger.debug('AnimationSettingsProvider initialized');
      logCurrentSettings();
      isFirstRender.current = false;
    }
  }, [debug, logCurrentSettings]);
  
  // Create context value
  const contextValue = useMemo<AnimationSettingsContextType>(() => {
    return {
      animationsEnabled,
      intensity,
      toggleAnimations,
      setIntensity,
      prefersReducedMotion,
      getDurationMultiplier,
      getDelayMultiplier,
      shouldAnimate,
      logCurrentSettings
    };
  }, [
    animationsEnabled, 
    intensity, 
    toggleAnimations, 
    setIntensity,
    prefersReducedMotion,
    getDurationMultiplier,
    getDelayMultiplier,
    shouldAnimate,
    logCurrentSettings
  ]);

  return (
    <AnimationSettingsContext.Provider value={contextValue}>
      {children}
    </AnimationSettingsContext.Provider>
  );
};

export default AnimationSettingsProvider; 
